import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, useAuthStore } from "@/modules/auth";
import { FYSwitcher } from "@/modules/financialYear";
import { useLogout } from "@/api/hooks/auth";
import { ChevronDown, KeyRound, LogOut, Menu } from "@/shared/icons";
import { useUIStore } from "@/shared/store/ui";
import { ChangePasswordDialog } from "@/modules/auth/components/ChangePasswordDialog";
import { NotificationBell } from "@/modules/notifications";
import { SessionTimer } from "./SessionTimer";
import { NavbarTimerPill } from "@/modules/timetracking";
import { useTodayAttendance } from "@/api/hooks/attendance";
import { LogoutAttendancePrompt } from "@/modules/attendance/components/LogoutAttendancePrompt";
import { useQueryClient } from "@tanstack/react-query";

export function TopBar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const logout = useLogout();
  const forceLogout = useAuthStore((s) => s.forceLogout);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutPromptOpen, setLogoutPromptOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const toggleMobileSidebar = useUIStore((s) => s.toggleMobileSidebar);

  const role = user?.role;
  const attendanceRoleEligible =
    role === "admin" || role === "operations" || role === "employee" || role === "hr";
  const todayAtt = useTodayAttendance(attendanceRoleEligible);
  const hasOpenAttendance =
    !!todayAtt.data?.attendance && !todayAtt.data.attendance.markedOutAt;

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const initials = (user?.fullName ?? "")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const finalizeLogout = async () => {
    try {
      await logout.mutateAsync();
    } catch {
      // ignore — server clears cookie regardless
    }
    forceLogout("user");
    navigate("/login", { replace: true });
  };

  const onLogout = () => {
    setMenuOpen(false);
    if (hasOpenAttendance) {
      setLogoutPromptOpen(true);
      return;
    }
    void finalizeLogout();
  };

  const onLogoutPromptCancel = () => setLogoutPromptOpen(false);

  const onSignOutOnly = () => {
    setLogoutPromptOpen(false);
    void finalizeLogout();
  };

  const onSignedOutAfterMarkOut = async () => {
    setLogoutPromptOpen(false);
    // Mark-out invalidates attendance queries; await one settle so state is fresh.
    await qc.invalidateQueries({ queryKey: ["attendance", "today"] });
    await finalizeLogout();
  };

  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar-hamburger"
        onClick={toggleMobileSidebar}
        aria-label="Toggle navigation"
      >
        <Menu size={20} />
      </button>
      <Link to="/dashboard" className="flex items-center" aria-label="Workman home">
        <img src="/workman-logo.svg" alt="Workman" className="topbar-logo h-12" />
      </Link>

      <div className="mx-auto flex-1" />


      <div className="flex items-center gap-2">
        <NavbarTimerPill />
        <SessionTimer />
        {user?.role !== "client" && <FYSwitcher />}
        <NotificationBell />
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-transparent px-2 hover:border-[var(--border)] hover:bg-[var(--gray-50)]"
            aria-label="Account menu"
            aria-expanded={menuOpen}
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[var(--gray-200)] text-[11px] font-semibold text-gray-700">
              {initials || "??"}
            </span>
            <span className="topbar-user-meta hidden sm:flex flex-col items-start leading-tight">
              <span
                className="text-[13px] font-medium text-gray-900 truncate"
                style={{ maxWidth: 140 }}
                title={user?.fullName ?? "Guest"}
              >
                {user?.fullName ?? "Guest"}
              </span>
              <span className="rounded-sm bg-[var(--gray-150)] px-1 text-[9px] font-bold uppercase tracking-wider text-gray-600">
                {user?.role ?? "—"}
              </span>
            </span>
            <ChevronDown size={14} aria-hidden />
          </button>
          {menuOpen ? (
            <div
              role="menu"
              className="absolute right-0 top-11 z-[var(--z-popover)] w-56 overflow-hidden rounded-md border border-[var(--border)] bg-white shadow-[var(--shadow-2)]"
            >
              <div className="border-b border-[var(--border)] px-3 py-2 text-[12px] text-gray-500">
                {user?.email ?? ""}
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  setChangePwOpen(true);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-gray-700 hover:bg-[var(--gray-50)]"
              >
                <KeyRound size={14} aria-hidden /> Change password
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={onLogout}
                className="flex w-full items-center gap-2 border-t border-[var(--border)] px-3 py-2 text-left text-[13px] text-[var(--danger-700)] hover:bg-[var(--danger-50)]"
              >
                <LogOut size={14} aria-hidden /> Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <LogoutAttendancePrompt
        open={logoutPromptOpen}
        onCancel={onLogoutPromptCancel}
        onSignOutOnly={onSignOutOnly}
        onSignedOutAfterMarkOut={onSignedOutAfterMarkOut}
      />
      <ChangePasswordDialog open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </header>
  );
}
